import React, { useCallback, useEffect } from "react";
import {
	Box,
	ButtonLayout,
	ButtonPrimary,
	Carousel,
	Divider,
	Grid,
	GridItem,
	IconButton,
	IconChevronLeftRegular,
	IconChevronRightRegular,
	PageBullets,
	Stack,
	Tag,
	Text2,
	Text3,
	Text6,
	skinVars,
	useScreenSize,
} from "@telefonica/mistica";
import ButtonGroup from "@telefonica/mistica/dist/button-group";
import { data, dataSecondAccount } from "./data";
import { strings } from "../../localization";
import { Image } from "@chakra-ui/react";
import { useAnalytics } from "../../hooks";
import { getFFSecondQiTechAccount } from "../../services/SystemConfiguration";

interface IProps {
	title: string;
	subtitle?: string;
	buttonTitle: string;
	alignButton?: "center" | "left";
	onSubmit(): void;
}

const { texts, textsSecondAccount, tag } = strings.components.howToAuthorizeSlide;

export const HowToAuthorizeSlide: React.FC<IProps> = (props) => {
	const [isSecondAccount, setIsSecondAccount] = React.useState(false);
	const { title, subtitle, buttonTitle, alignButton, onSubmit } = props;
	const { isDesktopOrBigger } = useScreenSize();
	const { addAnalyticEvent } = useAnalytics();

	const [currentIndex, setCurrentIndex] = React.useState(0);
	const numPages = data.length;

	const isPrevButtonDisabled = currentIndex === 0;
	const isNextButtonDisabled = currentIndex === numPages - 1;

	const currentSlideImages = isSecondAccount ? dataSecondAccount[currentIndex] : data[currentIndex];
	const currentSlideText = isSecondAccount ? textsSecondAccount[currentIndex] : texts[currentIndex];

	const handleNextSlide = (): void => {
		setCurrentIndex((prevIndex) => (prevIndex + 1) % numPages);
	};

	const handlePrevSlide = (): void => {
		setCurrentIndex((prevIndex) => (prevIndex - 1 + numPages) % numPages);
	};

	const handleAnalyticInteraction = (description: string): void => {
		addAnalyticEvent({
			component: "body",
			action: "click:button",
			description,
			step: "home:sliders:etapas-antecipacao",
		}, { applySanitize: true });
	};

	// Busca valor da feature flag isVisibleColdSimulationFGTS
	const fetchFeatureFlag = useCallback(async (): Promise<void> => {
		const ffIsSecondQiTechAccount = await getFFSecondQiTechAccount();
		setIsSecondAccount(ffIsSecondQiTechAccount);
	}, []);

	useEffect(() => {
		fetchFeatureFlag();
	}, []);

	return (
		<Grid columns={isDesktopOrBigger ? 2 : 1}>
			<GridItem columnStart={1} rowStart={1}>
				<Box paddingBottom={32} paddingTop={isDesktopOrBigger ? 16 : 0}>
					<Stack space={8}>
						<Text6 color={skinVars.colors.textPrimary}>{title}</Text6>
						<Text3 light color={skinVars.colors.textSecondary}>
							{subtitle}
						</Text3>
					</Stack>
				</Box>
			</GridItem>
			<GridItem columnStart={isDesktopOrBigger ? 2 : 1} rowStart={isDesktopOrBigger ? 1 : 2}>
				<Box paddingBottom={isDesktopOrBigger ? 0 : 32}>
					<Grid alignContent="start" justifyContent="center" alignItems="start" justifyItems="center">
						<Carousel
							itemStyle={{
								display: "flex",
								flexDirection: "row",
								justifyContent: "center",
								alignItems: "center",
								paddingLeft: isDesktopOrBigger ? 106 : 0,
							}}
							itemsPerPage={1}
							items={[
								<Image src={currentSlideImages} key={currentIndex} w={"100%"} maxW={260} h={480} />,
							]}
						/>
					</Grid>
				</Box>
			</GridItem>
			<GridItem rowStart={isDesktopOrBigger ? 1 : 3} columnStart={1} alignSelf="end">
				<Box paddingX={isDesktopOrBigger ? 0 : 16} paddingY={16}>
					<Stack space={8}>
						<Tag dataAttributes={{ testid: "currentIndex" }} type="promo">
							{tag + (currentIndex + 1).toString()}
						</Tag>
						<Text3 regular color={skinVars.colors.textPrimary}>
							{currentSlideText.titleInfo}
						</Text3>
						<Text2 regular color={skinVars.colors.textSecondary}>
							{currentSlideText.subtitleInfo}
						</Text2>
					</Stack>
				</Box>
				<Stack space={isDesktopOrBigger ? 24 : 64}>
					<Divider />
					<Grid columns={2} justifyContent="space-between">
						<ButtonGroup
							primaryButton={
								<IconButton
									dataAttributes={{ testid: "primaryButton" }}
									disabled={isPrevButtonDisabled}
									backgroundColor={skinVars.colors.brand}
									size={40}
									style={{
										borderRadius: 99999,
										display: "flex",
										flexDirection: "row",
										justifyContent: "center",
										alignItems: "center",
										opacity: isPrevButtonDisabled ? 0.5 : 1,
									}}
									onPress={() => {
										handlePrevSlide(); handleAnalyticInteraction("slide-anterior");
									}}
								>
									{<IconChevronLeftRegular size={24} color={skinVars.colors.inverse} style={{ margin: 8 }} />}
								</IconButton>
							}
							secondaryButton={
								<IconButton
									dataAttributes={{ testid: "secondaryButton" }}
									disabled={isNextButtonDisabled}
									backgroundColor={skinVars.colors.brand}
									size={40}
									style={{
										borderRadius: 99999,
										display: "flex",
										flexDirection: "row",
										justifyContent: "center",
										alignItems: "center",
										opacity: isNextButtonDisabled ? 0.5 : 1,
									}}
									onPress={() => {
										handleNextSlide(); handleAnalyticInteraction("proximo-slide");
									}}
								>
									{<IconChevronRightRegular size={24} color={skinVars.colors.inverse} style={{ margin: 8 }} />}
								</IconButton>
							}
						/>
						<PageBullets numPages={numPages} currentIndex={currentIndex} />
					</Grid>
				</Stack>
				<Box paddingTop={isDesktopOrBigger ? 40 : 32} paddingBottom={isDesktopOrBigger ? 16 : 0}>
					<ButtonLayout align={alignButton}>
						<ButtonPrimary onPress={onSubmit} style={{ width: alignButton === "center" ? "100%" : "auto", borderRadius: "999px" }}>
							{buttonTitle}
						</ButtonPrimary>
					</ButtonLayout>
				</Box>
			</GridItem>
		</Grid>
	);
};
